import { useState, MouseEvent, KeyboardEvent } from 'react';
import DatePicker from 'react-datepicker';
import {
  Controller,
  Control,
  FieldErrors,
  FieldValues,
  FieldPath,
  UseControllerProps,
} from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import DatepickerHeader from './DatepickerHeader';
import { IconClose, IconCalendar } from '../Icon/Index';

interface CustomDatepickerRHFProps<T extends FieldValues>
  extends UseControllerProps<T> {
  name: FieldPath<T>;
  rules: Object;
  errors: FieldErrors;
  control: Control<T>;
  defaultValue?: T[keyof T];
  className?: string;
  containerClassName?: string;
  formLabel?: string;
  formSubLabel?: string;
  inputHelpBlock?: string;
  isRequired?: boolean;
  minDate?: Date;
  format?: string;
  showTimeSelect?: boolean;
  showMonthYearPicker?: boolean;
  onInputChange?: (e: Date | null) => void;
  onResetDate?: () => void;
  disabled?: boolean;
  placeholder?: string;
  onChangeRaw?: (
    e:
      | MouseEvent<HTMLElement, globalThis.MouseEvent>
      | KeyboardEvent<HTMLElement>,
  ) => void;
}

const DatepickerRHF = <T extends FieldValues>(
  props: CustomDatepickerRHFProps<T>,
) => {
  const {
    name,
    defaultValue,
    className = '',
    containerClassName,
    formLabel,
    formSubLabel,
    inputHelpBlock,
    isRequired = false,
    onInputChange,
    onResetDate,
    showTimeSelect = false,
    showMonthYearPicker = false,
    minDate,
    format = 'dd MMMM yyyy',
    disabled = false,
    rules,
    control,
    errors,
    placeholder = '',
    onChangeRaw,
  } = props;

  const [isFocus, setIsFocus] = useState<boolean>(false);

  return (
    <div
      className={`theme-form-input-container ${
        containerClassName !== undefined ? containerClassName : ''
      }`}
    >
      {formLabel && (
        <label className='theme-form-input-label' htmlFor={`field-${name}`}>
          {formLabel} {isRequired && <span>*</span>}
        </label>
      )}
      {formSubLabel && (
        <label className='theme-form-sublabel'>{formSubLabel}</label>
      )}
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={rules || {}}
        render={({ field: { onChange, value } }) => (
          <DatePicker
            selected={value}
            disabled={disabled}
            dateFormat={format}
            showTimeSelect={showTimeSelect}
            minDate={minDate}
            showIcon
            icon={
              !isFocus && !value ? (
                <IconCalendar />
              ) : (
                <IconClose onClick={() => onResetDate && onResetDate()} />
              )
            }
            useWeekdaysShort={true}
            showMonthYearPicker={showMonthYearPicker}
            placeholderText={placeholder}
            wrapperClassName='theme-custom-datepicker-wrapper'
            calendarClassName='theme-custom-datepicker'
            className={`theme-form-control ${name in errors && 'theme-form-control-has-error'} ${className}`}
            renderCustomHeader={props => <DatepickerHeader {...props} />}
            onChange={e => {
              onChange(e);
              onInputChange && onInputChange(e);
            }}
            onCalendarOpen={() => setIsFocus(true)}
            onCalendarClose={() => {
              setIsFocus(false);
            }}
            toggleCalendarOnIconClick
            onChangeRaw={e => {
              if (e) {
                e.preventDefault();
                onChangeRaw && onChangeRaw(e);
              }
            }}
          />
        )}
      />
      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }) => {
          return <div className='theme-form-text form-error'>{message}</div>;
        }}
      />
      {inputHelpBlock && (
        <span className='theme-form-helpblock'>{inputHelpBlock}</span>
      )}
    </div>
  );
};

export default DatepickerRHF;
